import React, { useMemo } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
} from "@material-ui/core";
import { makeStyles, withStyles, useTheme, alpha } from "@material-ui/core/styles";
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info,
  ExpandMore,
  ExpandLess,
  EmojiObjects,
} from "@material-ui/icons";
import { green, red, orange, yellow, blue, grey } from "@material-ui/core/colors";

import {
  validateTemplate,
  SEVERITY,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  LIMITS,
} from "../../utils/templateValidator";

// ========================== STYLES ==========================

const useStyles = makeStyles((theme) => ({
  root: {
    border: "1px solid",
    borderRadius: 12,
    overflow: "hidden",
    transition: "border-color 0.3s ease",
  },
  scoreHeader: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  scoreCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    border: "3px solid",
    flexShrink: 0,
  },
  scoreValue: {
    fontSize: "1.4rem",
    fontWeight: 700,
    lineHeight: 1,
  },
  scoreLabel: {
    fontSize: "0.6rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statusText: {
    flex: 1,
    marginLeft: theme.spacing(2),
  },
  summaryChips: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  issuesList: {
    padding: theme.spacing(0, 2, 2, 2),
    maxHeight: 300,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  issueItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  issueSeverityDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginTop: 6,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  issueContent: {
    flex: 1,
  },
  issueMessage: {
    fontSize: "0.82rem",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  issueTip: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
  },
  tipIcon: {
    fontSize: "0.85rem",
    marginTop: 1,
    color: orange[700],
    flexShrink: 0,
  },
  charCounter: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === "dark" ? alpha("#fff", 0.05) : theme.palette.grey[50],
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  charItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.7rem",
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 2, 0.5, 2),
  },
}));

// ========================== SCORE COLORS ==========================

// Os tons 50/100 do MUI não mudam com palette.type: no escuro o fundo vira translúcido
function getScoreColor(score, isDark) {
  if (isDark) {
    let main = red[300];
    if (score >= 85) main = green[400];
    else if (score >= 70) main = green[300];
    else if (score >= 50) main = orange[400];
    return { main, light: alpha(main, 0.15), border: alpha(main, 0.5) };
  }
  if (score >= 85) return { main: green[600], light: green[50], border: green[200] };
  if (score >= 70) return { main: green[400], light: green[50], border: green[100] };
  if (score >= 50) return { main: orange[600], light: orange[50], border: orange[200] };
  if (score >= 25) return { main: red[400], light: red[50], border: red[100] };
  return { main: red[700], light: red[50], border: red[300] };
}

function getScoreIcon(score, color) {
  if (score >= 70) return <CheckCircle style={{ color, fontSize: 20 }} />;
  if (score >= 25) return <Warning style={{ color, fontSize: 20 }} />;
  return <ErrorIcon style={{ color, fontSize: 20 }} />;
}

// Chip com fundo claro + texto escuro; no tema escuro, fundo translúcido + texto claro
function tintStyle(isDark, palette, bgShade, fgShade) {
  return {
    backgroundColor: isDark ? alpha(palette[500], 0.2) : palette[bgShade],
    color: isDark ? palette[300] : palette[fgShade],
  };
}

function getSeverityIcon(severity) {
  switch (severity) {
    case SEVERITY.CRITICAL:
      return <ErrorIcon style={{ color: red[700], fontSize: 16 }} />;
    case SEVERITY.HIGH:
      return <ErrorIcon style={{ color: red[400], fontSize: 16 }} />;
    case SEVERITY.MEDIUM:
      return <Warning style={{ color: orange[600], fontSize: 16 }} />;
    case SEVERITY.LOW:
      return <Info style={{ color: blue[500], fontSize: 16 }} />;
    case SEVERITY.INFO:
      return <Info style={{ color: grey[500], fontSize: 16 }} />;
    default:
      return null;
  }
}

// ========================== CUSTOM PROGRESS ==========================

const ScoreProgress = withStyles((theme) => ({
  root: {
    height: 8,
    borderRadius: 4,
  },
  colorPrimary: {
    backgroundColor: theme.palette.type === "dark" ? theme.palette.divider : theme.palette.grey[200],
  },
  bar: {
    borderRadius: 4,
    transition: "width 0.5s ease, background-color 0.5s ease",
  },
}))(LinearProgress);

// ========================== COMPONENT ==========================

const TemplateValidationPanel = ({ formData, sampleValues = {}, expanded: externalExpanded }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const [expanded, setExpanded] = React.useState(true);

  const validationResult = useMemo(() => {
    return validateTemplate({
      name: formData.name || "",
      category: formData.category || "UTILITY",
      language: formData.language || "pt_BR",
      bodyText: formData.bodyText || "",
      headerText: formData.headerFormat === "TEXT" ? (formData.headerText || "") : "",
      headerFormat: formData.headerFormat || "NONE",
      headerMediaUrl: formData.headerMediaUrl || "",
      footerText: formData.footerText || "",
      buttons: formData.buttons || [],
      sampleValues,
    });
  }, [formData, sampleValues]);

  const { score, status, statusLabel, issues, summary, charCounts, variables } = validationResult;
  const colors = getScoreColor(score, isDark);
  const chipStyle = (palette, bgShade, fgShade) => ({
    ...tintStyle(isDark, palette, bgShade, fgShade),
    fontSize: "0.68rem",
    height: 22,
  });

  const isExpanded = externalExpanded !== undefined ? externalExpanded : expanded;

  const actionableIssues = issues.filter(i => i.severity !== SEVERITY.INFO);
  const infoIssues = issues.filter(i => i.severity === SEVERITY.INFO);

  return (
    <Box className={classes.root} style={{ borderColor: colors.border }}>
      {/* Score Header */}
      <Box
        className={classes.scoreHeader}
        style={{ backgroundColor: colors.light, cursor: "pointer" }}
        onClick={() => setExpanded(!isExpanded)}
      >
        <Box className={classes.scoreCircle} style={{ borderColor: colors.main }}>
          <Typography className={classes.scoreValue} style={{ color: colors.main }}>
            {score}%
          </Typography>
          <Typography className={classes.scoreLabel} style={{ color: colors.main }}>
            Aprov.
          </Typography>
        </Box>

        <Box className={classes.statusText}>
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            {getScoreIcon(score, colors.main)}
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {statusLabel}
            </Typography>
          </Box>

          <Box className={classes.summaryChips}>
            {summary.critical > 0 && (
              <Chip
                size="small"
                label={`${summary.critical} Crítico${summary.critical > 1 ? "s" : ""}`}
                style={chipStyle(red, 100, 800)}
              />
            )}
            {summary.high > 0 && (
              <Chip
                size="small"
                label={`${summary.high} Alto${summary.high > 1 ? "s" : ""}`}
                style={chipStyle(red, 50, 600)}
              />
            )}
            {summary.medium > 0 && (
              <Chip
                size="small"
                label={`${summary.medium} Médio${summary.medium > 1 ? "s" : ""}`}
                style={chipStyle(orange, 50, 800)}
              />
            )}
            {summary.low > 0 && (
              <Chip
                size="small"
                label={`${summary.low} Recomendaç${summary.low > 1 ? "ões" : "ão"}`}
                style={chipStyle(blue, 50, 700)}
              />
            )}
            {summary.info > 0 && (
              <Chip
                size="small"
                label={`${summary.info} Dica${summary.info > 1 ? "s" : ""}`}
                style={chipStyle(grey, 100, 700)}
              />
            )}
            {summary.total === 0 && (
              <Chip
                size="small"
                label="Nenhum problema encontrado"
                style={chipStyle(green, 50, 700)}
              />
            )}
          </Box>
        </Box>

        <IconButton size="small">
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Progress Bar */}
      <ScoreProgress
        variant="determinate"
        value={score}
        style={{
          "& .MuiLinearProgress-bar": { backgroundColor: colors.main },
        }}
        className={classes.progressBar}
      />
      <Box style={{ height: 8, backgroundColor: isDark ? theme.palette.divider : "#e0e0e0", position: "relative", marginTop: -8 }}>
        <Box
          style={{
            height: "100%",
            width: `${score}%`,
            backgroundColor: colors.main,
            borderRadius: "0 4px 4px 0",
            transition: "width 0.5s ease, background-color 0.5s ease",
          }}
        />
      </Box>

      <Collapse in={isExpanded}>
        {/* Character Counters */}
        <Box className={classes.charCounter}>
          {["header", "body", "footer"].map(field => {
            const label = { header: "Cabeçalho", body: "Corpo", footer: "Rodapé" }[field];
            const { current, max } = charCounts[field];
            const pct = Math.min((current / max) * 100, 100);
            const isOver = current > max;
            return (
              <Box key={field} className={classes.charItem}>
                <Typography variant="caption" style={{ fontWeight: 500, minWidth: 60 }}>
                  {label}:
                </Typography>
                <Typography
                  variant="caption"
                  style={{
                    color: isOver
                      ? (isDark ? red[300] : red[600])
                      : current > max * 0.8
                        ? (isDark ? orange[300] : orange[600])
                        : (isDark ? theme.palette.text.secondary : grey[600]),
                    fontWeight: isOver ? 700 : 400,
                  }}
                >
                  {current}/{max}
                </Typography>
              </Box>
            );
          })}
          {variables.length > 0 && (
            <Box className={classes.charItem}>
              <Typography variant="caption" style={{ fontWeight: 500 }}>
                Variáveis: {variables.length}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Issues List */}
        {actionableIssues.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              Problemas a Corrigir ({actionableIssues.length})
            </Typography>
            <Box className={classes.issuesList}>
              {actionableIssues.map((issue, idx) => (
                <Box key={idx} className={classes.issueItem}>
                  <Box
                    className={classes.issueSeverityDot}
                    style={{ backgroundColor: SEVERITY_COLORS[issue.severity] }}
                  />
                  <Box className={classes.issueContent}>
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      {getSeverityIcon(issue.severity)}
                      <Typography className={classes.issueMessage}>
                        {issue.message}
                      </Typography>
                    </Box>
                    {issue.tip && (
                      <Typography className={classes.issueTip}>
                        <EmojiObjects className={classes.tipIcon} />
                        {issue.tip}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Tips / Info */}
        {infoIssues.length > 0 && (
          <>
            <Divider />
            <Typography className={classes.sectionTitle}>
              Dicas de Melhoria ({infoIssues.length})
            </Typography>
            <Box className={classes.issuesList}>
              {infoIssues.map((issue, idx) => (
                <Box key={idx} className={classes.issueItem}>
                  <Box
                    className={classes.issueSeverityDot}
                    style={{ backgroundColor: SEVERITY_COLORS[issue.severity] }}
                  />
                  <Box className={classes.issueContent}>
                    <Typography className={classes.issueMessage} style={{ color: isDark ? theme.palette.text.secondary : grey[600] }}>
                      {issue.message}
                    </Typography>
                    {issue.tip && (
                      <Typography className={classes.issueTip}>
                        <EmojiObjects className={classes.tipIcon} />
                        {issue.tip}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* All Good */}
        {issues.length === 0 && (
          <Box p={2} textAlign="center">
            <CheckCircle style={{ color: green[500], fontSize: 32 }} />
            <Typography variant="body2" style={{ color: isDark ? green[300] : green[700], fontWeight: 500, marginTop: 4 }}>
              Template está em conformidade com as políticas do WhatsApp!
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default TemplateValidationPanel;
